import type { DtoV1SubjectPropertySubjectPropertySummaryResponse } from '~/__generated__/api';
import CurrencyInput from '~/common/components/Inputs/CurrencyInput';
import Stack from '~/common/components/Stacks/Stack';
import Typography from '~/common/components/Texts/Typography';
import { formatCurrency } from '~/common/util/format';

type ExpenseRow = {
  label: string;
  amount: number | null | undefined;
};

type ProposedPaymentAndRentalInformationSectionProps = {
  rowToEdit: DtoV1SubjectPropertySubjectPropertySummaryResponse;
  grossMonthlyRent: number | null;
  adjustedMonthlyGrossIncome: number | null;
  netRentalIncome: number | null;
  isGrossMonthlyRentEditable: boolean;
  onGrossMonthlyRentChange: (value: string) => void;
  isWriteAccessDisabled?: boolean;
};

type MonthlyExpenseKey =
  | 'proposedPrincipalAndInterestPayment'
  | 'proposedOtherFinancing'
  | 'proposedMortgageInsurance'
  | 'proposedHazardInsurance'
  | 'proposedFloodInsurance'
  | 'proposedUtility'
  | 'proposedRealEstateTaxes'
  | 'proposedAssociationDues'
  | 'proposedGroundRent'
  | 'proposedSpecialAssessments'
  | 'proposedMaintenance';

const MONTHLY_EXPENSE_ROW_ENTRIES: Array<readonly [string, MonthlyExpenseKey]> =
  [
    ['First Mortgage (P & I)', 'proposedPrincipalAndInterestPayment'],
    ['Other Financing (P & I)', 'proposedOtherFinancing'],
    ['Mortgage Insurance', 'proposedMortgageInsurance'],
    ['Hazard Insurance', 'proposedHazardInsurance'],
    ['Flood Insurance', 'proposedFloodInsurance'],
    ['Utilities', 'proposedUtility'],
    ['Real Estate Taxes', 'proposedRealEstateTaxes'],
    ['HOA Dues', 'proposedAssociationDues'],
    ['Lease / Ground Rent', 'proposedGroundRent'],
    ['Special Assessments', 'proposedSpecialAssessments'],
    ['Maintenance', 'proposedMaintenance'],
  ];

const MONTHLY_EXPENSE_ROWS: Array<
  Omit<ExpenseRow, 'amount'> & {
    key: MonthlyExpenseKey;
  }
> = MONTHLY_EXPENSE_ROW_ENTRIES.map(([label, key]) => ({
  label,
  key,
}));

const amountFormatter = Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const sanitizeAmount = (value: number | null | undefined) => Number(value ?? 0);

const formatAmountWithoutSymbol = (value: number | null | undefined) =>
  amountFormatter.format(sanitizeAmount(value));

const toExpenseTestId = (label: string) =>
  `monthly-expense-${label
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/(^-|-$)/g, '')}`;

const ExpenseColumn = ({ rows }: { rows: ExpenseRow[] }) => (
  <>
    {rows.map((expense) => (
      <Stack
        key={expense.label}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
          paddingBottom: '0.15rem',
          borderBottom: '1px solid',
          borderColor: 'gray.200',
        }}
      >
        <Typography variant="body">{expense.label}</Typography>
        <Typography variant="body" data-testid={toExpenseTestId(expense.label)}>
          {formatAmountWithoutSymbol(expense.amount)}
        </Typography>
      </Stack>
    ))}
  </>
);

export default function ProposedPaymentAndRentalInformationSection({
  rowToEdit,
  grossMonthlyRent,
  adjustedMonthlyGrossIncome,
  netRentalIncome,
  isGrossMonthlyRentEditable,
  onGrossMonthlyRentChange,
  isWriteAccessDisabled = false,
}: Readonly<ProposedPaymentAndRentalInformationSectionProps>) {
  const monthlyExpenseRows: ExpenseRow[] = MONTHLY_EXPENSE_ROWS.map((row) => ({
    label: row.label,
    amount: rowToEdit[row.key],
  }));

  const leftMonthlyExpenseRows = monthlyExpenseRows.slice(0, 7);
  const rightMonthlyExpenseRows = monthlyExpenseRows.slice(7);
  const totalMonthlyExpenses = monthlyExpenseRows.reduce(
    (total, row) => total + sanitizeAmount(row.amount),
    0,
  );

  return (
    <Stack
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: { xs: '1.5rem', lg: '2rem' },
      }}
    >
      <Stack
        sx={{
          width: { xs: '100%', lg: '50%' },
          display: 'flex',
          flexDirection: 'column',
          borderRight: { xs: 'none', lg: '1px solid' },
          borderColor: 'gray.200',
        }}
      >
        <Typography
          variant="body"
          sx={{
            marginBottom: '1rem',
            fontSize: '1.3rem',
            fontWeight: 500,
          }}
        >
          Total Monthly Expenses
        </Typography>
        <Stack
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: '0.5rem', md: '2rem' },
          }}
        >
          <Stack
            sx={{ width: { xs: '100%', md: '46%' }, gap: '0.5rem' }}
            data-testid="monthly-expenses-left-column"
          >
            <ExpenseColumn rows={leftMonthlyExpenseRows} />
          </Stack>
          <Stack
            sx={{ width: { xs: '100%', md: '46%' }, gap: '0.5rem' }}
            data-testid="monthly-expenses-right-column"
          >
            <ExpenseColumn rows={rightMonthlyExpenseRows} />
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                marginTop: '0.25rem',
                paddingBottom: '0.15rem',
                borderBottom: '1px solid',
                borderColor: 'gray.200',
              }}
            >
              <Typography variant="body" sx={{ fontWeight: 700 }}>
                Total Expenses
              </Typography>
              <Typography
                variant="body"
                sx={{ fontWeight: 700 }}
                data-testid="monthly-expense-total-expenses"
              >
                {formatCurrency(totalMonthlyExpenses)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        sx={{
          width: { xs: '100%', lg: '42%' },
          minHeight: { xs: '0', lg: '26rem' },
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
        data-testid="monthly-expenses-calculator-section"
      >
        <Typography
          variant="body"
          sx={{
            fontWeight: 700,
            fontSize: '1.3rem',
          }}
        >
          Calculator
        </Typography>
        <Stack
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: '1rem',
            width: '100%',
          }}
        >
          <Stack sx={{ width: '100%' }}>
            <Typography variant="body" sx={{ fontWeight: 500, mb: '0.25rem' }}>
              Gross Monthly Rent
            </Typography>
            <CurrencyInput
              id="grossMonthlyRent"
              data-testid="grossMonthlyRent"
              value={grossMonthlyRent ?? ''}
              onChange={onGrossMonthlyRentChange}
              isDisabled={!isGrossMonthlyRentEditable || isWriteAccessDisabled}
            />
          </Stack>
          <Stack sx={{ width: '100%' }}>
            <Typography variant="body" sx={{ fontWeight: 500, mb: '0.25rem' }}>
              Adj. Monthly Gross Income
            </Typography>
            <CurrencyInput
              id="adjustedMonthlyGrossIncome"
              data-testid="adjustedMonthlyGrossIncome"
              value={adjustedMonthlyGrossIncome ?? ''}
              isDisabled
            />
          </Stack>
          <Stack sx={{ width: '100%' }}>
            <Typography variant="body" sx={{ fontWeight: 500, mb: '0.25rem' }}>
              Net Rental Income
            </Typography>
            <CurrencyInput
              id="netRentalIncome"
              data-testid="netRentalIncome"
              value={netRentalIncome ?? ''}
              isDisabled
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
